import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import Anchor from 'common-ui-components/dist/components/Atoms/Anchor/Anchor';
import Notification from 'common-ui-components/dist/components/Atoms/Notification';
import Image from 'common-ui-components/dist/components/Atoms/Image/Image';
import { urlSanitize } from 'common-ui-components/lib/core/utils/common';
import { isErrorCodeExistInLabel } from '../../../utils/utilFunctions';
import { getLabelWithComponent, isUserLoggedIn } from '../../../utils/utils';
import './ProductRecommendations.scss';
import QuickOrder from '../QuickOrder/QuickOrder';

const ProductRecommendationsTableItem = ({
  skuData,
  labels,
  config,
  productRecommendationAddToCart,
  shoppingCartError,
  productRecommendationData,
  rowIndex,
  rowKey,
  productRecommendationTitleClick,
}) => {
  const {
    skuLabel,
    contactWaters,
    yourPrice,
    listPrice: listPriceLabel,
    unavailableMaterialDescription,
  } = labels;
  const { contactUsLink, removeAllItemsIconUrl, unavailableIconUrl } = config;
  const {
    skuNumber,
    name,
    thumbnailUrl,
    pageUrl,
    purchasable,
    activityDetails1,
    isLoading = false,
  } = skuData;
  const [inputValue, setInputValue] = useState();

  const contactUsAnchor = (
    <Anchor
      className="row cart-inline-error-contact-us-link"
      elementLocator="add-to-cart-invalid-sku-error-contact-us-link"
      to={contactUsLink}
    >
      {contactWaters}
    </Anchor>
  );
  const inlineError = isErrorCodeExistInLabel(labels, shoppingCartError)
    ? getLabelWithComponent(labels[shoppingCartError.code], [inputValue, contactUsAnchor])
    : '';
  const renderPricing = () => {
    const { listPrice, custPrice } = productRecommendationData;
    const isLoggedInUser = isUserLoggedIn();
    const priceLabels = isLoggedInUser ? yourPrice : listPriceLabel;
    let price = listPrice;
    if (isLoggedInUser) {
      price = typeof custPrice !== 'undefined' && !isEmpty(custPrice) ? custPrice : listPrice;
    }

    return (
      <div className="product-recommendation-price-container" data-locator="price-container">
        <span className="product-recommendation-price-label" data-locator="sku-price-label">
          {`${priceLabels}:`}
        </span>
        <span className="product-recommendation-price" data-locator="sku-price">
          {price || ''}
        </span>
      </div>
    );
  };

  const getPageUrl = () => {
    const criteria = get(activityDetails1, 'criteria.title', '');
    const recipeName = get(activityDetails1, 'campaign.recipe.name', '');
    const pageName = window.location.pathname.split('/').pop();
    const query = `?criteria=${criteria}&product=${skuNumber}&at_pageType=${pageName}&at_activityexperience=${recipeName}`;
    return `${pageUrl}${query}`;
  };
  const onTitleLinkClick = e => {
    if (e) {
      e.preventDefault();
    }
    const url = urlSanitize(getPageUrl());
    productRecommendationTitleClick([productRecommendationData]);
    setTimeout(() => {
      window.open(url, '_self');
    }, 500);
  };
  return (
    <li
      className="cmp-product-compare-result__card-body"
      data-locator={`product-compare-product-body-${rowIndex}`}
      key={rowKey}
    >
      <div
        className="cmp-product-compare-result__card-image"
        data-locator="product-compare-product-image"
      >
        <Image
          className="skutile-image"
          src={thumbnailUrl}
          alt={name}
          elementLocator="product-compare-image"
        />
      </div>
      <div
        className="skutile-sku-list-code pr-sku-number"
        data-locator="product-compare-product-number"
      >
        {`${skuLabel}: ${skuNumber}`}
      </div>
      <div
        className="skutile-row pr-title cmp-product-compare-result__card-title category-title"
        data-locator="product-recommendation-product-name"
      >
        {!isEmpty(pageUrl) ? (
          <Anchor
            // to={getPageUrl()}
            onClick={e => onTitleLinkClick(e)}
            elementLocator="Anchor"
          >
            {name}
          </Anchor>
        ) : (
          <span>{name}</span>
        )}
      </div>
      <div className="buy-info">
        {purchasable && (
          <div className="product-recommendation-add-to-cart-section">
            {renderPricing()}
            <div className="product-recommendation-add-to-cart">
              <QuickOrder
                skuNumber={skuNumber}
                skuNumCustomClass="sku-hidden"
                qtyCustomClass="qty-mb"
                pageId="product-recommendation-add-to-cart"
                elementLocator="product-recommendation-add-to-cart"
                inputPlaceholder="SKU"
                qtyPlaceholder="Qty"
                submitButtonLabel="Add To Cart"
                {...{
                  onSubmit: (skuValue, qty) => {
                    setInputValue(skuValue);
                    productRecommendationAddToCart(skuValue, qty, true);
                  },
                }}
              />
            </div>
          </div>
        )}
        {!purchasable && !isLoading && (
          <Notification
            description={unavailableMaterialDescription}
            icon={unavailableIconUrl}
            variation="attention"
            elementLocator="notification-container-attention-unavailable"
            className="cmp-notification-wrapper"
          />
        )}
        {inlineError && !!inputValue && (
          <div className="cart-inline-notification">
            <Notification
              variation="inline"
              type="error"
              description={inlineError}
              icon={removeAllItemsIconUrl}
              className="col-xs-12"
              elementLocator="notification-inline-error"
            />
          </div>
        )}
      </div>
    </li>
  );
};

ProductRecommendationsTableItem.propTypes = {
  labels: PropTypes.objectOf([PropTypes.string]),
  config: PropTypes.objectOf([PropTypes.string]),
  skuData: PropTypes.shape({
    skuNumber: PropTypes.string,
    name: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    pageUrl: PropTypes.string,
    purchasable: PropTypes.bool,
    isLoading: PropTypes.bool,
    activityDetails1: PropTypes.objectOf([PropTypes.string]),
  }),
  productRecommendationData: PropTypes.shape({
    code: PropTypes.string,
    addToCartDisabled: PropTypes.bool,
    purchasable: PropTypes.bool,
    isLoading: PropTypes.bool,
    custPrice: PropTypes.string,
    custValue: PropTypes.string,
    listPrice: PropTypes.string,
    listValue: PropTypes.string,
    currencyCode: PropTypes.string,
  }),
  productRecommendationAddToCart: PropTypes.func,
  shoppingCartError: PropTypes.objectOf([PropTypes.string]),
  rowIndex: PropTypes.number,
  rowKey: PropTypes.string,
  productRecommendationTitleClick: PropTypes.func,
};

ProductRecommendationsTableItem.defaultProps = {
  labels: {},
  config: {},
  skuData: {
    skuNumber: '',
    name: '',
    thumbnailUrl: '',
    pageUrl: '',
    purchasable: true,
    isLoading: true,
    activityDetails1: {},
  },
  productRecommendationData: {
    code: '',
    addToCartDisabled: false,
    purchasable: true,
    isLoading: true,
    custPrice: '',
    custValue: '',
    listPrice: '',
    listValue: '',
    currencyCode: '',
  },
  shoppingCartError: {},
  rowIndex: 0,
  rowKey: '',
  productRecommendationAddToCart: () => {
    /* default productRecommendationAddToCart handler */
  },
  productRecommendationTitleClick: () => {
    /* default productRecommendationTitleClick handler */
  },
};

export default ProductRecommendationsTableItem;
